/**
 * Zusatzleistungen auf der Produktseite (Versandschutz, Prioritaetsversand).
 *
 * Der Haken legt die Leistung sofort in den Warenkorb, das Wegnehmen holt
 * sie wieder heraus. Der Kaufknopf des Themes wird dabei nicht angefasst.
 *
 * ── Warum nicht beim Kauf, sondern beim Anhaken ────────────────────────
 *
 * Der erste Versuch hing sich an den Kaufknopf: Klick abfangen, Extras
 * einwerfen, Knopf erneut ausloesen. Das ging schief, weil das Theme
 * seinen submit-Handler ueber `on:submit` am selben Formular registriert —
 * und zwar frueher als eigenes Skript. Bei einem Event am Ziel-Element
 * laufen alle Listener in Registrierungsreihenfolge, ein
 * stopPropagation() kommt also zu spaet. Ergebnis: Das Theme hat beim
 * ersten Klick schon abgeschickt, der erneute Aufruf schickte ein zweites
 * Mal — das Motiv lag doppelt im Korb.
 *
 * Deshalb jetzt getrennt: Der Kaufknopf gehoert dem Theme, der Haken
 * gehoert diesem Skript. Beide fassen sich nicht an.
 *
 * ── Warum der Warenkorb trotzdem nicht selbst gebaut wird ──────────────
 *
 * Es gab hier schon einen Eigenbau, der den Drawer leer aufgehen liess:
 * Das Theme rendert ihn aus Section-HTML, das beim Hinzufuegen
 * mitgeschickt werden muss. Genau das passiert hier — die Sections werden
 * angefordert und dem Theme im Standard-Event uebergeben. Gezeichnet wird
 * weiterhin ausschliesslich vom Theme.
 */

/** Section-IDs, aus denen das Theme den Warenkorb zeichnet. Ohne sie
 *  bekommt das Theme nichts zum Neuzeichnen und der Drawer bleibt leer. */
function warenkorbSektionen() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((teil) => teil.dataset.sectionId)
    .filter(Boolean)
    .join(',');
}

async function korbHolen() {
  const antwort = await fetch(`${window.Theme?.routes?.cart_url || '/cart'}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!antwort.ok) throw new Error(`Warenkorb nicht lesbar (${antwort.status})`);
  return antwort.json();
}

/**
 * Meldet dem Theme, dass sich der Warenkorb geaendert hat — mit der
 * Event-Klasse des Themes selbst, damit Zaehler und Drawer sich so
 * aktualisieren wie nach einem normalen Kauf.
 */
async function themeBescheidGeben(zeilen, aktion, sektionen) {
  try {
    const { CartLinesUpdateEvent } = await import('@shopify/events');
    const abgelegt = CartLinesUpdateEvent.createPromise();

    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: aktion,
        context: 'product',
        lines: zeilen,
        promise: abgelegt.promise,
      })
    );

    const korb = await korbHolen();
    abgelegt.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(korb),
      detail: {
        items: korb.items,
        source: 'produkt-extras',
        itemCount: korb.item_count,
        sections: sektionen,
        didError: false,
      },
    });
  } catch (fehler) {
    // Die Ware liegt trotzdem richtig im Korb — nur die Anzeige hinkt
    // bis zur naechsten Aktion hinterher.
    console.warn('[extras] Theme nicht benachrichtigt:', fehler);
  }
}

async function hineinlegen(variantenId) {
  const antwort = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({
      items: [{ id: Number(variantenId), quantity: 1 }],
      sections: warenkorbSektionen(),
    }),
  });
  if (!antwort.ok) throw new Error(`Hinzufuegen fehlgeschlagen (${antwort.status})`);
  const ergebnis = await antwort.json();

  await themeBescheidGeben([{ merchandiseId: String(variantenId), quantity: 1 }], 'add', ergebnis.sections);
}

async function herausnehmen(variantenId) {
  const antwort = await fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({
      id: String(variantenId),
      quantity: 0,
      sections: warenkorbSektionen(),
    }),
  });
  if (!antwort.ok) throw new Error(`Entfernen fehlgeschlagen (${antwort.status})`);
  const ergebnis = await antwort.json();

  await themeBescheidGeben([{ merchandiseId: String(variantenId), quantity: 0 }], 'remove', ergebnis.sections);
}

/** Haken an den tatsaechlichen Warenkorb angleichen — nach dem Neuladen
 *  der Seite soll dastehen, was wirklich drin liegt. */
async function hakenAngleichen(kasten) {
  try {
    const korb = await korbHolen();
    const drin = new Set(korb.items.map((zeile) => String(zeile.variant_id)));
    kasten.querySelectorAll('input[type="checkbox"]').forEach((feld) => {
      feld.checked = drin.has(String(feld.dataset.variantenId));
    });
  } catch {
    /* Ohne Warenkorb-Antwort bleiben die Haken, wie sie sind. */
  }
}

function verdrahten(kasten) {
  if (kasten.dataset.verdrahtet) return;
  kasten.dataset.verdrahtet = '1';

  hakenAngleichen(kasten);

  // Der Warenkorb kann sich auch anderswo aendern — im Drawer lassen sich
  // Zeilen direkt entfernen. Ohne das hier bliebe der Haken gesetzt,
  // obwohl die Leistung nicht mehr im Korb liegt.
  document.addEventListener('shopify:cart:lines-update', (ereignis) => {
    if (ereignis.detail?.source === 'produkt-extras') return;

    // Traegt das Ereignis eine Zusage, erst danach nachsehen — sonst
    // liest man den Warenkorb, bevor der Server ihn geaendert hat. Ohne
    // Zusage direkt nachsehen, statt gar nicht: Nicht jeder Absender
    // liefert eine mit, und ein stehengebliebener Haken waere schlimmer
    // als eine Abfrage zu viel.
    const zusage = ereignis.promise;
    if (zusage?.then) {
      zusage.then(() => hakenAngleichen(kasten)).catch(() => {});
    } else {
      hakenAngleichen(kasten);
    }
  });

  kasten.addEventListener('change', async (ereignis) => {
    const feld = ereignis.target;
    if (!(feld instanceof HTMLInputElement) || feld.type !== 'checkbox') return;

    const id = feld.dataset.variantenId;
    if (!id) return;

    // Waehrend die Anfrage laeuft gesperrt, sonst erzeugt schnelles
    // Klicken zwei Zeilen derselben Leistung.
    feld.disabled = true;
    kasten.classList.add('pd__extras--laedt');

    try {
      if (feld.checked) {
        await hineinlegen(id);
      } else {
        await herausnehmen(id);
      }
    } catch (fehler) {
      console.warn('[extras]', fehler);
      feld.checked = !feld.checked; // Haken zurueck auf den echten Stand
    } finally {
      feld.disabled = false;
      kasten.classList.remove('pd__extras--laedt');
    }
  });
}

function start() {
  document.querySelectorAll('[data-produkt-extras]').forEach(verdrahten);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start);
} else {
  start();
}
